import { Button, Input, Typography } from 'antd'
import { useEffect, useState } from 'react'

import { restClient } from '@/Core'
import { ViewRow } from '@/Ship/Components/Detail/ViewRow/ViewRow'
import { StatusBadge } from '@/Ship/Components/UI/StatusBadge/StatusBadge'
import { formatAppDate } from '@/Ship/Helpers/appTime'
import { notify } from '@/Ship/Helpers/notify'
import { OPTION_MILEAGE_CONTROL_ENABLED, readMileageOptions } from '@/Ship/Helpers/portalOptions'

import { MILEAGE_DEVIATION_OPTIONS } from '../Contracts/MileageDeviation'
import { formatKm, formatKmDelta, formatPercentDelta } from '../Helpers/shiftFormat'

const { Text } = Typography

/** Як на бекенді (ReviewMileageDeviationAction::MAX_COMMENT_LENGTH) */
const MAX_COMMENT = 2000

/** Поля картки зміни, з яких складається блок */
export interface MileageValues {
    mileage?: number | null
    wialonMileage?: number | null
    mileageDeviationStatus?: string | null
    mileageDeviationKm?: number | null
    mileageDeviationPercent?: number | null
    mileageReviewComment?: string | null
    mileageReviewedAt?: string | null
    mileageReviewedBy?: { fullName?: string | null; shortName?: string | null } | null
}

interface Props {
    shiftUuid: string
    values: MileageValues
    /** Позначити перевіреним — диспетчер, коли відхилення чекає перевірки (`_permissions.mileageReview`) */
    canReview: boolean
    /** «Отримати з Wialon» — диспетчер, у ТЗ є ідентифікатор Wialon (`_permissions.wialonMileageFetch`) */
    canFetchWialon?: boolean
    /** Після розгляду чи запиту пробігу — перечитати картку */
    onReviewed: () => void
}

const errorText = (err: any, fallback: string): string => {
    const violations = err?.response?.violations
    if (Array.isArray(violations) && violations[0]?.message) return violations[0].message
    return err?.response?.message || fallback
}

/**
 * Контроль пробігу завершеної зміни: пробіг за одометром проти пробігу за Wialon, розбіжність
 * і її статус. Суттєву розбіжність диспетчер перевіряє й пояснює коментарем.
 */
export const MileageControl = ({ shiftUuid, values, canReview, canFetchWialon = false, onReviewed }: Props) => {
    const [comment, setComment] = useState('')
    const [busy, setBusy] = useState(false)
    const [fetching, setFetching] = useState(false)
    // Вмикач з «Налаштувань порталу»; поки не завантажено — вважаємо ввімкненим (як бекенд без опції)
    const [enabled, setEnabled] = useState(true)

    useEffect(() => {
        let alive = true
        const load = async () => {
            try {
                const result: any = await restClient.get('/services/tenant/options', { searchParams: { limit: 300 } })
                if (alive) setEnabled(readMileageOptions(result?.items ?? [])[OPTION_MILEAGE_CONTROL_ENABLED])
            } catch {
                // Не вдалося — лишаємо «ввімкнено»: блок покаже статус зміни як є
            }
        }
        void load()
        return () => {
            alive = false
        }
    }, [])

    const status = values.mileageDeviationStatus
    const option = MILEAGE_DEVIATION_OPTIONS.find((item) => item.value === status)

    const review = async () => {
        setBusy(true)
        try {
            await restClient.post(`/services/shifts/${shiftUuid}/mileage-review`, { json: { comment } })
            notify.success('Розбіжність пробігу позначено перевіреною')
            setComment('')
            onReviewed()
        } catch (err) {
            notify.error(errorText(err, 'Не вдалося позначити розбіжність перевіреною'))
            // Імовірно, стан уже інший (розглянув інший диспетчер, перерахувався пробіг) — показати актуальний
            onReviewed()
        } finally {
            setBusy(false)
        }
    }

    // Пробіг за Wialon зараз (без очікування фонової задачі); перезаписує внесене значення
    const fetchWialon = async () => {
        setFetching(true)
        try {
            const shift: any = await restClient.post(`/services/shifts/${shiftUuid}/wialon-mileage`)
            notify.success(`Пробіг за Wialon: ${formatKm(shift?.wialonMileage)}`)
        } catch (err) {
            notify.error(errorText(err, 'Не вдалося отримати пробіг з Wialon'))
        } finally {
            setFetching(false)
            onReviewed()
        }
    }

    const reviewer = values.mileageReviewedBy?.fullName || values.mileageReviewedBy?.shortName

    return (
        <>
            <ViewRow label="Пробіг за одометром">{formatKm(values.mileage)}</ViewRow>
            <ViewRow label="Пробіг за Wialon">
                {formatKm(values.wialonMileage)}
                {canFetchWialon && (
                    <Button
                        size="small"
                        style={{ marginLeft: 12 }}
                        loading={fetching}
                        onClick={() => void fetchWialon()}
                        data-testid="wialon-mileage-fetch"
                    >
                        Отримати з Wialon
                    </Button>
                )}
            </ViewRow>
            {option ? (
                <>
                    <ViewRow label="Розбіжність">
                        {formatKmDelta(values.mileageDeviationKm)}
                        {values.mileageDeviationPercent !== null && values.mileageDeviationPercent !== undefined && (
                            <Text type="secondary"> ({formatPercentDelta(values.mileageDeviationPercent)})</Text>
                        )}
                    </ViewRow>
                    <ViewRow label="Статус">
                        <span data-testid="mileage-deviation-status">
                            <StatusBadge option={option} fallbackLabel={option.label} />
                        </span>
                    </ViewRow>
                </>
            ) : (
                <ViewRow label="Статус">
                    <Text type="secondary" data-testid="mileage-deviation-empty">
                        {!enabled
                            ? 'Контроль пробігу вимкнено в налаштуваннях порталу'
                            : values.wialonMileage === null || values.wialonMileage === undefined
                              ? 'Немає пробігу за Wialon — порівнювати нема з чим'
                              : 'Не перевірялось: зміну закрито чи змінено, коли контроль був вимкнений'}
                    </Text>
                </ViewRow>
            )}
            {!enabled && option && (
                <ViewRow label="">
                    <Text type="secondary">
                        Контроль пробігу вимкнено: статус лишився з часу, коли він працював, нові правки не
                        перевіряються
                    </Text>
                </ViewRow>
            )}

            {status === 'reviewed' && (
                <>
                    <ViewRow label="Перевірив">
                        {[reviewer, formatAppDate(values.mileageReviewedAt, 'DD.MM.YYYY, HH:mm')]
                            .filter(Boolean)
                            .join(', ')}
                    </ViewRow>
                    <ViewRow label="Результат перевірки">
                        <span style={{ whiteSpace: 'pre-wrap' }}>{values.mileageReviewComment}</span>
                    </ViewRow>
                </>
            )}

            {canReview && (
                <ViewRow label="Результат перевірки">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Input.TextArea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            maxLength={MAX_COMMENT}
                            autoSize={{ minRows: 2, maxRows: 6 }}
                            placeholder="Що з'ясували: помилка в одометрі, трекер не працював тощо"
                            data-testid="mileage-review-comment"
                        />
                        <div>
                            <Button
                                type="primary"
                                loading={busy}
                                disabled={comment.trim() === ''}
                                onClick={() => void review()}
                                data-testid="mileage-review-submit"
                            >
                                Позначити перевіреною
                            </Button>
                        </div>
                    </div>
                </ViewRow>
            )}
        </>
    )
}
